import { FISCAL_CALENDAR_NAME, getCustomCalendarDateBucketBoundaries, getCustomCalendarGuid as getCalendarGuid, getCustomCalendarName, getStartEpoch, GREGORIAN_CALENDAR_NAME, } from '@thoughtspot/custom-calendar-service';
import { NameValuePairs } from '@thoughtspot/custom-style-service';
import * as dateUtil from '@thoughtspot/date-format-util';
import { jsonConstants } from '@thoughtspot/json-constants';
import { includeMilliSeconds, isAttributeNfEnabled, isRenamingColumnsEnabled, } from '@thoughtspot/session-service';
import _ from 'lodash';
import { ColumnMetricsDefinition, MetricDefinition } from './column-metrics';
import { AnswerColumnEffectiveType, AnswerColumnType, ChartSpecificColumnType, ColumnAggregationType, ColumnTimeBucket, ColumnType, FalconDataType, } from './types/prism';
export const getId = (col) => col.id;
export const getColumnIds = (columns) => _.map(columns, col => getId(col));
export const getColumnId = (column) => (column ? getId(column) : null);
export const getBaseColumnName = (col) => col.name;
export const getTitle = (col) => col.name;
export const isMeasure = (col) => col.type === AnswerColumnEffectiveType.Measure;
export const isAttribute = (col) => col.type === AnswerColumnEffectiveType.Attribute;
export const isMeasureValues = (col) => col.chartSpecificColumnType === ChartSpecificColumnType.MeasureValues;
export const isMeasureName = (col) => col.chartSpecificColumnType === ChartSpecificColumnType.MeasureNames;
export const isAdditive = (col) => col.isAdditive;
export const getEffectiveDataType = (col) => col.dataType;
const isAggregationApplied = (col) => !!col.isAggregateApplied;
const _getEffectiveAggrType = (col) => col.aggregationType;
export const getReferencedColumnHeaders = (col) => col.referencedColumns;
export const isGroupingColumn = (col) => col.isGroupBy;
export const isGrowth = (col) => col.type === AnswerColumnEffectiveType.Measure && col.showGrowth;
export const isGrowthBy = (col) => isAttribute(col) && !isGroupingColumn(col);
export const getUniqueCount = (col) => col.uniqueValues;
export const isGeoColumn = (col) => !!col.geoConfig;
export const getBucketization = (col) => col.timeBucket;
export const getCustomCalendarGuidFromColumn = (col) => col.calendarGuid;
export const getCustomOrder = (col) => col.customOrder;
export const columnFormulaId = (col) => _.get(col, 'formulaId');
export const isInAnswerFormula = (col) => !!columnFormulaId(col);
export const isFilterColumn = (col) => col.baseColumnType === AnswerColumnType.FilterColumn;
export var AggregateTypes;
(function (AggregateTypes) {
    AggregateTypes["NONE"] = "NONE";
    AggregateTypes["SUM"] = "SUM";
    AggregateTypes["AVERAGE"] = "AVERAGE";
    AggregateTypes["MIN"] = "MIN";
    AggregateTypes["MAX"] = "MAX";
    AggregateTypes["COUNT"] = "COUNT";
    AggregateTypes["COUNT_DISTINCT"] = "COUNT_DISTINCT";
    AggregateTypes["STD_DEVIATION"] = "STD_DEVIATION";
    AggregateTypes["TABLE_AGGR"] = "TABLE_AGGR";
    AggregateTypes["VARIANCE"] = "VARIANCE";
})(AggregateTypes || (AggregateTypes = {}));
export const isWholeNumberAggregation = (aggrType) => aggrType === ColumnAggregationType.CountDistinct ||
    aggrType === ColumnAggregationType.Count;
export const isDoubleNumberAggregation = (aggrType) => aggrType === ColumnAggregationType.Variance ||
    aggrType === ColumnAggregationType.StdDeviation ||
    aggrType === ColumnAggregationType.Average;
export const supportsAggregationChange = (col) => isMeasure(col) && isAggregationApplied(col);
export const getEffectiveAggregateType = (col) => {
    if (!isAggregationApplied(col) || !_getEffectiveAggrType(col)) {
        return ColumnAggregationType.None;
    }
    return _getEffectiveAggrType(col);
};
export const getTrueEffectiveAggregateType = (col) => {
    const effAggrType = _getEffectiveAggrType(col);
    if (!effAggrType) {
        return ColumnAggregationType.None;
    }
    return effAggrType;
};
export const numericFalconDataTypes = [
    FalconDataType.Double,
    FalconDataType.Int64,
    FalconDataType.Int32,
    FalconDataType.Float,
];
export const isNumeric = (col) => {
    const type = getEffectiveDataType(col);
    return (type === FalconDataType.Int64 ||
        type === FalconDataType.Int32 ||
        type === FalconDataType.Float ||
        type === FalconDataType.Double);
};
export const isBaseNumeric = (col) => numericFalconDataTypes.includes(col.dataType);
export const isDoubleColumn = (col) => getEffectiveDataType(col) === FalconDataType.Double;
export const isFloat = (col) => getEffectiveDataType(col) === FalconDataType.Float || isDoubleColumn(col);
export const isEffectivelyNumeric = (col) => isMeasure(col) || (isAdditive(col) && isBaseNumeric(col));
export const isEffectivelyNonNumeric = (col) => !isEffectivelyNumeric(col);
export const isColumnValidForNumberFormatting = (col) => {
    if (isAttributeNfEnabled()) {
        return isBaseNumeric(col) && !isDateNumColumn(col) && !isGeoColumn(col);
    }
    return isEffectivelyNumeric(col);
};
export const getEffectiveDateNumDataType = (col) => {
    switch (col.timeBucket) {
        case ColumnTimeBucket.DayOfWeek:
            return dateUtil.dateNumTypes.DATE_NUM_DAY_OF_WEEK;
        case ColumnTimeBucket.DayOfMonth:
            return dateUtil.dateNumTypes.DATE_NUM_DAY_IN_MONTH;
        case ColumnTimeBucket.DayOfQuarter:
            return dateUtil.dateNumTypes.DATE_NUM_DAY_IN_QUARTER;
        case ColumnTimeBucket.DayOfYear:
            return dateUtil.dateNumTypes.DATE_NUM_DAY_IN_YEAR;
        case ColumnTimeBucket.WeekOfMonth:
            return dateUtil.dateNumTypes.DATE_NUM_WEEK_IN_MONTH;
        case ColumnTimeBucket.WeekOfQuarter:
            return dateUtil.dateNumTypes.DATE_NUM_WEEK_IN_QUARTER;
        case ColumnTimeBucket.WeekOfYear:
            return dateUtil.dateNumTypes.DATE_NUM_WEEK_IN_YEAR;
        case ColumnTimeBucket.MonthOfQuarter:
            return dateUtil.dateNumTypes.DATE_NUM_MONTH_IN_QUARTER;
        case ColumnTimeBucket.MonthOfYear:
            return dateUtil.dateNumTypes.DATE_NUM_MONTH_IN_YEAR;
        case ColumnTimeBucket.QuarterOfYear:
            return dateUtil.dateNumTypes.DATE_NUM_QUARTER_IN_YEAR;
        default:
            return undefined;
    }
};
export const isDateColumn = (col) => getEffectiveDataType(col) === FalconDataType.Date ||
    getEffectiveDataType(col) === FalconDataType.DateTime;
export const isTimeColumn = (col) => getEffectiveDataType(col) === FalconDataType.Time ||
    getEffectiveDataType(col) === FalconDataType.DateTime;
export const isDateTimeColumn = (col) => getEffectiveDataType(col) === FalconDataType.DateTime;
export const getTimeBucket = (col) => _.get(dateUtil.timeBuckets, col.timeBucket, dateUtil.timeBuckets.NO_BUCKET);
const isDateNumTimeBucket = (col) => {
    return [
        ColumnTimeBucket.HourOfDay,
        ColumnTimeBucket.DayOfWeek,
        ColumnTimeBucket.DayOfMonth,
        ColumnTimeBucket.DayOfQuarter,
        ColumnTimeBucket.DayOfYear,
        ColumnTimeBucket.WeekOfMonth,
        ColumnTimeBucket.WeekOfQuarter,
        ColumnTimeBucket.WeekOfYear,
        ColumnTimeBucket.MonthOfQuarter,
        ColumnTimeBucket.MonthOfYear,
        ColumnTimeBucket.QuarterOfYear,
    ].includes(col.timeBucket);
};
export const isDateNumColumn = (col) => {
    return isNumeric(col) && isAttribute(col) && isDateNumTimeBucket(col);
};
export const isDateFamilyColumn = (col) => {
    return isDateColumn(col) || isDateNumColumn(col);
};
export const supportsDateBucketizationChange = (col) => isDateColumn(col) || isTimeColumn(col) || isDateNumColumn(col);
export const hasCustomCalendar = (col) => isDateFamilyColumn(col) && !!getCustomCalendarGuidFromColumn(col);
export const getEffectiveColumnType = (col) => {
    if (isDateFamilyColumn(col)) {
        return ColumnType.Date;
    }
    if (isMeasure(col)) {
        return ColumnType.Measure;
    }
    return ColumnType.Attribute;
};
export const getCustomCalendarValueFromEpoch = (col, dateEpoch, displayToCustomCalendarValueMap) => {
    if (hasCustomCalendar(col) &&
        _.has(displayToCustomCalendarValueMap, dateEpoch)) {
        return displayToCustomCalendarValueMap[dateEpoch];
    }
    return null;
};
export const getDateBucketBoundaries = (col, epochValue, displayToCustomCalendarValueMap, translateString) => {
    const customCalendarValue = getCustomCalendarValueFromEpoch(col, epochValue, displayToCustomCalendarValueMap);
    if (customCalendarValue) {
        const customCalendarDateBucketBoundaries = getCustomCalendarDateBucketBoundaries(customCalendarValue);
        if (customCalendarDateBucketBoundaries) {
            return customCalendarDateBucketBoundaries;
        }
    }
    if (epochValue === translateString('NULL_VALUE_PLACEHOLDER_LABEL') ||
        epochValue === translateString('EMPTY_VALUE_PLACEHOLDER_LABEL')) {
        return [epochValue];
    }
    if (!isDateColumn(col)) {
        return null;
    }
    const timeBucket = getTimeBucket(col);
    const parsedIntegerEpochValue = parseInt(String(epochValue), 10);
    switch (timeBucket) {
        case dateUtil.timeBuckets.HOURLY:
            return dateUtil.getHourlyBoundaries(parsedIntegerEpochValue);
        case dateUtil.timeBuckets.DAILY:
            return [parsedIntegerEpochValue];
        case dateUtil.timeBuckets.WEEKLY:
            return dateUtil.getWeeklyBoundaries(parsedIntegerEpochValue);
        case dateUtil.timeBuckets.MONTHLY:
            return dateUtil.getMonthlyBoundaries(parsedIntegerEpochValue);
        case dateUtil.timeBuckets.QUARTERLY:
            return dateUtil.getQuarterlyBoundaries(parsedIntegerEpochValue);
        case dateUtil.timeBuckets.YEARLY:
            return dateUtil.getYearlyBoundaries(parsedIntegerEpochValue);
        default:
            return null;
    }
};
export const getDataRecency = (col) => {
    const recency = _.get(col, 'baseDataRecency');
    if (!_.isNumber(recency)) {
        return '';
    }
    return dateUtil.epochToTimeAgoString(Number(recency) * 1000);
};
export const showDateFinancialYearFormat = (col) => {
    const supportedBucketizations = [
        dateUtil.timeBuckets.QUARTERLY,
        dateUtil.timeBuckets.YEARLY,
    ];
    const currentBucketization = getTimeBucket(col);
    return supportedBucketizations.some(supportedBucketization => {
        return supportedBucketization === currentBucketization;
    });
};
export const convertValueToBackend = (column, dataValue) => {
    if ((isDateColumn(column) || isTimeColumn(column)) &&
        !isNaN(dataValue) &&
        !includeMilliSeconds()) {
        return Math.floor(dataValue / 1000);
    }
    return dataValue;
};
export const convertValueFromBackend = (col, dataValue, aggrType = getEffectiveAggregateType(col)) => {
    let value = getStartEpoch(dataValue);
    if (hasCustomCalendar(col) && value !== null && !isNaN(value)) {
        if (!includeMilliSeconds() && isDateColumn(col)) {
            value *= 1000;
        }
        return value;
    }
    if (!includeMilliSeconds() &&
        (isDateColumn(col) || isTimeColumn(col)) &&
        !isNaN(dataValue) &&
        dataValue !== null &&
        !isWholeNumberAggregation(aggrType)) {
        return dataValue * 1000;
    }
    return dataValue;
};
export const shouldShowCustomCalendarNameInTitle = (col) => {
    const columnCalendarId = getCustomCalendarGuidFromColumn(col);
    if (!hasCustomCalendar(col) ||
        (columnCalendarId &&
            columnCalendarId === getCalendarGuid(GREGORIAN_CALENDAR_NAME))) {
        return false;
    }
    const notShowCustomCalendarForDateBuckets = [
        dateUtil.timeBuckets.DAILY,
        dateUtil.timeBuckets.NO_BUCKET,
    ];
    return (!!getCustomCalendarName(columnCalendarId) &&
        !notShowCustomCalendarForDateBuckets.includes(getTimeBucket(col)));
};
export const isCalendarGregorianOrFiscal = (col) => {
    if (isDateFamilyColumn(col)) {
        const customCalenderGuid = getCustomCalendarGuidFromColumn(col);
        return (!customCalenderGuid ||
            customCalenderGuid === getCalendarGuid(GREGORIAN_CALENDAR_NAME) ||
            customCalenderGuid === getCalendarGuid(FISCAL_CALENDAR_NAME));
    }
    return false;
};
export const getCustomCalendarMapForColumn = (col, displayToCustomCalendarValueMap) => {
    if (hasCustomCalendar(col) &&
        _.has(displayToCustomCalendarValueMap, [col.id])) {
        return displayToCustomCalendarValueMap[col.id];
    }
    return {};
};
export const getAggregateLabel = (aggrType, getTranslatedText) => {
    const aggregateString = AggregateTypes[aggrType] ===
        AggregateTypes.NONE
        ? ''
        : `aggregateTypes.${AggregateTypes[aggrType]}`;
    return getTranslatedText(aggregateString);
};
const getAggregateTitle = (title, aggrType, getTranslatedText) => {
    const aggrText = getAggregateLabel(aggrType, getTranslatedText);
    return aggrText ? `${aggrText} ${title}` : title;
};
export const getTimeBucketizedTitle = (title, timeBucket, getTranslatedText) => {
    if (timeBucket === 'ms') {
        return title;
    }
    const bucketLabel = getTranslatedText(`timeBucketLabels.${timeBucket}`);
    return bucketLabel ? `${_.capitalize(bucketLabel)} ${title}` : title;
};
function getSupportedNonAdditiveFields() {
    return [AggregateTypes.COUNT_DISTINCT, AggregateTypes.COUNT];
}
export function getSupportedAggregateTypes(aggregateTypes, isAdditive) {
    if (!isAdditive) {
        return getSupportedNonAdditiveFields();
    }
    return aggregateTypes;
}
export const getAggregationTypeLabelsMap = (getTranslatedText, column) => {
    let aggregateTypes = Object.values(AggregateTypes);
    if (column) {
        aggregateTypes = getSupportedAggregateTypes(aggregateTypes, column.isAdditive);
    }
    const aggrTypeLabels = {};
    aggregateTypes.forEach(key => {
        const aggrType = AggregateTypes[key];
        if (aggrType !== AggregateTypes.NONE &&
            aggrType !== AggregateTypes.TABLE_AGGR) {
            aggrTypeLabels[aggrType] = getAggregateLabel(aggrType, getTranslatedText);
        }
    });
    return aggrTypeLabels;
};
export const getUnderlyingColumnName = (col) => {
    const referencedColumnHeaders = getReferencedColumnHeaders(col);
    const referencedColumnNames = _.map(referencedColumnHeaders, header => header.displayName);
    if (referencedColumnNames.length === 1) {
        return referencedColumnNames[0];
    }
    return getBaseColumnName(col);
};
export const getName = (col, getTranslatedText, skipPrefix = false) => {
    const colName = isInAnswerFormula(col)
        ? getBaseColumnName(col)
        : getUnderlyingColumnName(col);
    if (skipPrefix) {
        return colName;
    }
    let title = getAggregateTitle(colName, getEffectiveAggregateType(col), getTranslatedText);
    if (isGrowth(col) && !isInAnswerFormula(col)) {
        title = `${getTranslatedText('GROWTH_OF')} ${title}`;
    }
    if (supportsDateBucketizationChange(col)) {
        title = getTimeBucketizedTitle(colName, getTimeBucket(col), getTranslatedText);
    }
    if (shouldShowCustomCalendarNameInTitle(col)) {
        title = `${title} - ${_.capitalize(getCustomCalendarName(getCustomCalendarGuidFromColumn(col)))}`;
    }
    return title;
};
export const getSourceName = (col, getTranslatedText) => {
    if (isFilterColumn(col) || isInAnswerFormula(col)) {
        return getTranslatedText('FORMULA');
    }
    const referencedTableHeaders = col.referencedTables;
    const referencedTableNames = _.map(referencedTableHeaders, header => header.displayName);
    if (referencedTableNames.length === 1) {
        return referencedTableNames[0];
    }
    return getBaseColumnName(col);
};
export const getTooltipInformationModel = (col, getTranslatedText, columnNameSuffix) => {
    const sourcePart = getSourceName(col, getTranslatedText);
    let namePart = col.isUserDefinedTitle && isRenamingColumnsEnabled()
        ? col.name
        : getName(col, getTranslatedText);
    namePart = columnNameSuffix ? `${namePart} ${columnNameSuffix}` : namePart;
    const aggregationPart = getAggregateLabel(getEffectiveAggregateType(col), getTranslatedText);
    const recencyPart = getDataRecency(col);
    const model = new NameValuePairs();
    model.add(getTranslatedText('tooltip.NAME'), namePart);
    if (aggregationPart) {
        model.add(getTranslatedText('tooltip.AGGREGATION'), aggregationPart);
    }
    if (hasCustomCalendar(col)) {
        model.add(getTranslatedText('tooltip.CALENDAR'), getCustomCalendarName(col.calendarGuid));
    }
    model.add(getTranslatedText('tooltip.SOURCE'), sourcePart);
    if (recencyPart) {
        model.add(getTranslatedText('tooltip.UPDATED'), recencyPart);
    }
    return model;
};
export const getMetricsDefinition = (col) => {
    let metricDefinitionJson = null;
    if (Object.prototype.hasOwnProperty.call(col, jsonConstants.METRIC_DEFINITION_KEY)) {
        metricDefinitionJson = _.get(col, jsonConstants.METRIC_DEFINITION_KEY);
        const baseMetricDefinitionJson = _.get(col, jsonConstants.BASE_METRIC_DEFINITION_KEY);
        if (baseMetricDefinitionJson) {
            metricDefinitionJson = baseMetricDefinitionJson;
        }
    }
    const metricDefinition = new MetricDefinition(metricDefinitionJson?.row);
    if (!!metricDefinition && metricDefinition.row.length > 0) {
        return new ColumnMetricsDefinition(metricDefinition);
    }
    return null;
};
export const getColumnSortPriorityOrder = (sortInfoList, customSortedColumnIds) => {
    const priorityOrder = [];
    for (let i = 0; i < sortInfoList.length; i++) {
        const sortItem = sortInfoList[i];
        const index = customSortedColumnIds.findIndex(colId => colId === sortItem.columnId);
        if (index !== -1) {
            priorityOrder.push(index);
        }
    }
    return priorityOrder;
};
//# sourceMappingURL=column-utils.js.map